/**
 * Performance Rating Distribution Component (CR-6381)
 *
 * Displays a horizontal bar chart showing the distribution of suppliers
 * across rating bands (A/B/C/D/F/N/A). Bars are clickable to drill down
 * to the list of suppliers with that rating.
 */
import { useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import type { SupplierRatingThreshold } from '../../lib/api'

const NO_DATA = 'N/A'
const UNGRADED_COLOR = '#9ca3af' // gray-400

/**
 * CR-6269: what each bar is called and coloured — the customer's Supplier
 * Grade Thresholds (label + palette colour), the same bands the backend
 * counted the suppliers into. Was a fixed A–F palette with an
 * Excellent/Good/Fair/Poor/Failing legend that no settings page could change.
 */
export interface RatingBandMeta {
  label: string
  color: string
}

export function ratingBandMeta(bands: SupplierRatingThreshold[]): Record<string, RatingBandMeta> {
  const meta: Record<string, RatingBandMeta> = {}
  for (const band of bands) {
    meta[band.grade] = {
      label: band.display_label || band.label || band.grade,
      color: band.effective_color || band.color || UNGRADED_COLOR,
    }
  }
  meta[NO_DATA] = { label: 'No Data', color: UNGRADED_COLOR }
  return meta
}

/** Bars in band order, then No Data, then anything the payload carries that no band names. */
function ratingOrder(data: Record<string, number>, meta: Record<string, RatingBandMeta>): string[] {
  const known = Object.keys(meta).filter((r) => r in data)
  const extra = Object.keys(data).filter((r) => !(r in meta)).sort()
  return [...known, ...extra]
}

interface RatingBarProps {
  rating: string
  label: string
  color: string
  count: number
  maxCount: number
  onBarClick?: (rating: string) => void
}

function RatingBar({ rating, label, color, count, maxCount, onBarClick }: RatingBarProps) {
  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0
  const isClickable = !!onBarClick && count > 0

  const handleClick = useCallback(() => {
    if (onBarClick && count > 0) {
      onBarClick(rating)
    }
  }, [onBarClick, rating, count])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && onBarClick && count > 0) {
        onBarClick(rating)
      }
    },
    [onBarClick, rating, count]
  )

  return (
    <div className="flex items-center gap-3 mb-2">
      {/* Rating label */}
      <div className="w-10 text-sm font-medium text-right">{rating}</div>

      {/* Bar container */}
      <div className="flex-1 h-7 bg-gray-100 rounded overflow-hidden">
        <div
          className={`h-full flex items-center transition-all duration-300 ${
            isClickable ? 'cursor-pointer hover:brightness-110' : ''
          }`}
          style={{
            width: `${Math.max(percentage, count > 0 ? 8 : 0)}%`,
            minWidth: count > 0 ? '32px' : '0',
            backgroundColor: color,
          }}
          onClick={handleClick}
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onKeyDown={handleKeyDown}
          title={isClickable ? `Click to view ${count} supplier(s) rated ${label}` : undefined}
        >
          {count > 0 && (
            <span className="px-2 text-xs font-medium text-white">{count}</span>
          )}
        </div>
      </div>

      {/* Count label (for accessibility when bar is too small) */}
      <div className="w-8 text-sm text-muted-foreground text-right">{count}</div>
    </div>
  )
}

interface PerformanceRatingDistributionProps {
  /** Distribution data as {rating: count} */
  data: Record<string, number>
  /** Title for the chart card */
  title: string
  /** Callback when a rating bar is clicked */
  onBarClick?: (rating: string) => void
  /** CR-6269: the viewer's Supplier Grade Thresholds (from useRatingBands) */
  bands?: SupplierRatingThreshold[]
}

export function PerformanceRatingDistribution({
  data,
  title,
  onBarClick,
  bands = [],
}: PerformanceRatingDistributionProps) {
  const meta = ratingBandMeta(bands)

  // Calculate max count for scaling bars
  const maxCount = Math.max(...Object.values(data), 1)

  // Get total suppliers
  const totalSuppliers = Object.values(data).reduce((sum, count) => sum + count, 0)

  const ratings = ratingOrder(data, meta)

  if (ratings.length === 0 || totalSuppliers === 0) {
    return (
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No rating distribution data available
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="text-base">{title}</CardTitle>
          <span className="text-xs text-muted-foreground">
            {totalSuppliers} supplier{totalSuppliers !== 1 ? 's' : ''}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {ratings.map((rating) => (
            <RatingBar
              key={rating}
              rating={rating}
              label={meta[rating]?.label ?? rating}
              color={meta[rating]?.color ?? UNGRADED_COLOR}
              count={data[rating] || 0}
              maxCount={maxCount}
              onBarClick={onBarClick}
            />
          ))}
        </div>

        {/* Legend — one entry per configured grade, plus No Data */}
        <div className="mt-4 pt-3 border-t flex flex-wrap gap-3 text-xs text-muted-foreground justify-center">
          {Object.entries(meta).map(([grade, m]) => (
            <div key={grade} className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: m.color }} />
              <span>{grade === NO_DATA ? m.label : `${m.label} (${grade})`}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
